import { bffJson } from "@/shared/api/client";
import type { AssetClassResolver } from "@/domain/quant/portfolio-mapping";

export type { AssetClassResolver };

export async function getAssetClassResolver(
  mapIds: readonly string[],
): Promise<AssetClassResolver> {
  const distinct = [...new Set(mapIds.filter((id) => id !== ""))];
  const [catalogue, ...maps] = await Promise.all([
    getClassCatalogue(),
    ...distinct.map((id) => getClassMap(id)),
  ]);
  const mapById = new Map(distinct.map((id, i) => [id, maps[i]]));
  return (mapId, assetId, order) => {
    if (!mapId || !assetId) return null;
    const classIds = mapById.get(mapId)?.get(assetId) ?? [];
    for (const classId of classIds) {
      const row = catalogue.get(classId);
      if (row && row.order === order) return row.name;
    }
    return null;
  };
}

const CLASS_TTL_MS = 6 * 60 * 60 * 1000;

interface ClassRow {
  id?: string | number | null;
  name?: string | null;
  quant_asset_class_order_id?: string | number | null;
}

type Cached<T> = { value: T; expires: number };

export type ClassCatalogue = Map<string, { name: string; order: string }>;

let classCache: Cached<ClassCatalogue> | null = null;
const mapCache = new Map<string, Cached<Map<string, string[]>>>();
const inFlight = new Map<string, Promise<unknown>>();

export async function getClassCatalogue(): Promise<ClassCatalogue> {
  return once(
    "catalogue",
    () => (classCache && classCache.expires > Date.now() ? classCache.value : undefined),
    async () => {
      const rows = await bffJson<ClassRow[]>("v1/quant-asset-class");
      const byId: ClassCatalogue = new Map();
      for (const r of Array.isArray(rows) ? rows : []) {
        const id = r?.id != null ? String(r.id) : "";
        const name = (r?.name ?? "").trim();
        if (!id || !name) continue;
        byId.set(id, { name, order: String(r?.quant_asset_class_order_id ?? "") });
      }
      classCache = { value: byId, expires: Date.now() + CLASS_TTL_MS };
      return byId;
    },
  );
}

export async function getClassMap(mapId: string): Promise<Map<string, string[]>> {
  return once(
    `map:${mapId}`,
    () => {
      const hit = mapCache.get(mapId);
      return hit && hit.expires > Date.now() ? hit.value : undefined;
    },
    async () => {
      const detail = await bffJson<{
        items?: Array<{ quant_asset_id?: unknown; quant_asset_class_id?: unknown }>;
      }>(`v1/quant-asset-class-map/${encodeURIComponent(mapId)}`);
      const byAsset = new Map<string, string[]>();
      for (const item of Array.isArray(detail?.items) ? detail.items : []) {
        const assetId = item?.quant_asset_id != null ? String(item.quant_asset_id) : "";
        const classId = item?.quant_asset_class_id != null ? String(item.quant_asset_class_id) : "";
        if (!assetId || !classId) continue;
        const arr = byAsset.get(assetId) ?? [];
        arr.push(classId);
        byAsset.set(assetId, arr);
      }
      mapCache.set(mapId, { value: byAsset, expires: Date.now() + CLASS_TTL_MS });
      return byAsset;
    },
  );
}

async function once<T>(key: string, hit: () => T | undefined, load: () => Promise<T>): Promise<T> {
  const cached = hit();
  if (cached !== undefined) return cached;

  const running = inFlight.get(key) as Promise<T> | undefined;
  if (running) {
    try {
      return await running;
    } catch {}
    const filled = hit();
    if (filled !== undefined) return filled;
  }

  const attempt = load();
  inFlight.set(key, attempt);
  try {
    return await attempt;
  } finally {
    if (inFlight.get(key) === attempt) inFlight.delete(key);
  }
}
